import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Maximize, Bed, Bath, Car, Mail } from "lucide-react";
import { Property, formatPrice } from "@/data/properties";
import { Button } from "@/components/ui/button";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { optimizedImageUrl } from "@/lib/images";

interface Props {
  property: Property;
  index?: number;
}

export default function PropertyCard({ property, index = 0 }: Props) {
  const estadoColor = {
    disponible: "bg-emerald-500",
    reservada: "bg-amber-500",
    vendida: "bg-gray-500",
  };
  const estadoLabel = {
    disponible: "Disponible",
    reservada: "Reservada",
    vendida: "Vendida",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="h-full"
    >
      <article className="group apple-shadow flex h-full flex-col overflow-hidden rounded-lg border bg-card/90 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl">
        <div className="relative aspect-[4/3] overflow-hidden bg-card/80">
          <Link to={`/Propiedad?id=${property.id}`} aria-label={`Ver ${property.titulo}`} className="block h-full">
            <img
              src={optimizedImageUrl(property.imagen_principal, 720)}
              srcSet={`${optimizedImageUrl(property.imagen_principal, 480)} 480w, ${optimizedImageUrl(property.imagen_principal, 720)} 720w, ${optimizedImageUrl(property.imagen_principal, 960)} 960w`}
              sizes="(max-width: 768px) calc(100vw - 32px), (max-width: 1200px) 50vw, 400px"
              alt={property.titulo}
              className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.025]"
              loading="lazy"
            />
          </Link>
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-primary/50 to-transparent pointer-events-none" />
          <div className="absolute top-3 left-3 flex gap-2">
            {property.destacada && (
              <span className="rounded-sm bg-card/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-card-foreground backdrop-blur-sm">
                Exclusiva
              </span>
            )}
            <span className={`rounded-sm px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground ${estadoColor[property.estado]}`}>
              {estadoLabel[property.estado]}
            </span>
          </div>
          <span className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-card/90 text-card-foreground backdrop-blur-sm transition-transform group-hover:rotate-45">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Link to={`/Propiedad?id=${property.id}`} className="transition-colors hover:text-accent">
                <h3 className="text-lg font-semibold leading-snug text-foreground">{property.titulo}</h3>
              </Link>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-accent" />
                <span className="truncate">{property.ubicacion}</span>
              </p>
            </div>
            <p className="shrink-0 text-lg font-bold text-foreground">{formatPrice(property.precio)}</p>
          </div>

          <div className="mt-auto grid grid-cols-4 border-y py-4 text-xs text-muted-foreground">
            <span className="flex flex-col gap-1 border-r px-2 first:pl-0">
              <Maximize className="h-4 w-4 text-accent" />
              {property.metros_cuadrados} m²
            </span>
            <span className="flex flex-col gap-1 border-r px-2">
              <Bed className="h-4 w-4 text-accent" />
              {property.habitaciones} hab.
            </span>
            <span className="flex flex-col gap-1 border-r px-2">
              <Bath className="h-4 w-4 text-accent" />
              {property.banos} baños
            </span>
            <span className="flex flex-col gap-1 px-2">
              <Car className="h-4 w-4 text-accent" />
              {property.garaje ? `${property.plazas_garaje || 1} plazas` : "Sin garaje"}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-[1fr_auto_auto] gap-2">
            <Button asChild className="rounded-md">
              <Link to={`/Propiedad?id=${property.id}`}>Ver propiedad</Link>
            </Button>
            <Button asChild variant="outline" size="icon" className="rounded-md" title="Contactar por WhatsApp">
              <a
                href={`https://wa.me/34644100551?text=${encodeURIComponent(`Hola, os escribo a través de la web de Sercom. Estoy interesado/a en la propiedad "${property.titulo}" y me gustaría recibir más información o concertar una visita. ¡Gracias!`)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => import("@/lib/analytics").then(m => m.trackWhatsAppClick(property.id))}
                aria-label={`Contactar por WhatsApp sobre ${property.titulo}`}
              >
                <WhatsAppIcon className="h-6 w-6" />
              </a>
            </Button>
            <Button asChild variant="outline" size="icon" className="rounded-md" title="Contactar por email">
              <a
                href={`mailto:sercom.construction@gmail.com?subject=${encodeURIComponent(`Interés por ${property.titulo}`)}&body=${encodeURIComponent(`Hola,\n\nHe visto la propiedad "${property.titulo}" en la web de Sercom y me gustaría recibir más información o, si fuera posible, concertar una visita.\n\nQuedo a la espera de vuestra respuesta.\n\nUn saludo.`)}`}
                onClick={() => import("@/lib/analytics").then(m => m.trackEmailClick(property.id))}
                aria-label={`Contactar por email sobre ${property.titulo}`}
              >
                <Mail className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </article>
    </motion.div>
  );
}
